import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Container, Typography, Paper, Stack, Grid,
  FormControl, InputLabel, Select, MenuItem, TextField,
  Button, Avatar, Chip, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Divider, useTheme, alpha,
  Backdrop, CircularProgress, Snackbar,
  InputAdornment, Pagination, Badge
} from '@mui/material';
import {
  History as HistoryIcon,
  Person as PersonIcon,
  DateRange as DateRangeIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ArrowBack as BackIcon,
  CheckCircle as PresentIcon,
  Cancel as AbsentIcon,
  Schedule as LateIcon,
  HolidayVillage as LeaveIcon,
  FilterList as FilterIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useHistory } from 'react-router-dom';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import { baseUrl, config, requestID } from "../../../common/utils";

// Types
interface AttendanceRecord {
  id: number;
  date: string;
  waiterId: string;
  waiterName: string;
  createdBy: string;
  status: 'present' | 'absent' | 'late' | 'leave';
  check_in_time?: string;
  check_out_time?: string;
  createdAt: string;
}

interface FilterOptions {
  startDate: Date | null;
  endDate: Date | null;
  waiterId: string;
  status: 'all' | 'present' | 'absent' | 'late' | 'leave';
  search: string;
}

interface Waiter {
  user_id: string;
  fullName: string;
}

const AttendanceHistory: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<AttendanceRecord[]>([]);
  const [waiters, setWaiters] = useState<Array<{id: string, name: string}>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState<FilterOptions>({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)), // Last 7 days
    endDate: new Date(),
    waiterId: 'all',
    status: 'all',
    search: ''
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch waiters for filter dropdown
  const fetchWaiters = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/getWaiters`, formData, config);
      console.log("Waiters response:", response.data);
      
      if (response.data.status) {
        const waiterList = (response.data.data || []).map((w: any) => ({
          id: w.user_id,
          name: w.fullName
        }));
        setWaiters(waiterList);
      }
    } catch (error) {
      console.error("Fetch waiters error:", error);
    }
  };

  // Format date to YYYY-MM-DD
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format time for display
  const formatTime = (timeString?: string): string => {
    if (!timeString) return '--:--';
    if (timeString.includes(':')) {
      const parts = timeString.split(':');
      const hours = parseInt(parts[0]);
      const minutes = parts[1];
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const hour12 = hours % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    }
    return timeString;
  };

  // Fetch attendance history
  const fetchAttendanceHistory = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      
      // Add date filters
      if (filters.startDate) {
        formData.append("startDate", formatDate(filters.startDate));
      }
      
      if (filters.endDate) {
        formData.append("endDate", formatDate(filters.endDate));
      }

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/getAttendanceHistory`, formData, config);
      console.log("Attendance history response:", response.data);

      // Handle the nested data structure
      if (response.data.status && response.data.data?.data) {
        const mappedRecords = response.data.data.data.map((item: any) => ({
          id: item.id,
          date: item.date,
          waiterId: item.waiterId,
          waiterName: item.waiterName,
          createdBy: item.createdBy,
          status: item.status,
          check_in_time: item.check_in_time,
          check_out_time: item.check_out_time,
          createdAt: item.createdAt
        }));
        
        setRecords(mappedRecords);
        applyFilters(mappedRecords);
      } else {
        setRecords([]);
        setFilteredRecords([]);
      }
    } catch (error) {
      console.error("Fetch attendance error:", error);
      showNotification('Failed to fetch attendance history', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  const applyFilters = (data: AttendanceRecord[]) => {
    let filtered = [...data];

    // Filter by waiter
    if (filters.waiterId !== 'all') {
      filtered = filtered.filter(r => r.waiterId === filters.waiterId);
    }

    // Filter by status
    if (filters.status !== 'all') {
      filtered = filtered.filter(r => r.status === filters.status);
    }

    // Filter by search
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(r => 
        r.waiterName?.toLowerCase().includes(searchLower) ||
        r.status?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredRecords(filtered);
    setPage(1); // Reset to first page when filters change
  };

  // Handle filter changes
  const handleFilterChange = (field: keyof FilterOptions, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Apply filters when filters change
  useEffect(() => {
    applyFilters(records);
  }, [filters.waiterId, filters.status, filters.search, records]);

  // Fetch data when date range changes
  useEffect(() => {
    if (waiters.length > 0) {
      fetchAttendanceHistory();
    }
  }, [filters.startDate, filters.endDate]);

  // Initial load
  useEffect(() => {
    const loadInitialData = async () => {
      await fetchWaiters();
    };
    loadInitialData();
  }, []);

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const clearFilters = () => {
    setFilters({
      startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
      endDate: new Date(),
      waiterId: 'all',
      status: 'all',
      search: ''
    });
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAttendanceHistory().finally(() => setRefreshing(false));
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'present': return <PresentIcon />;
      case 'absent': return <AbsentIcon />;
      case 'late': return <LateIcon />;
      case 'leave': return <LeaveIcon />;
      default: return <PersonIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      present: '#10b981',
      absent: '#ef4444',
      late: '#f59e0b',
      leave: '#8b5cf6'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusChip = (record: AttendanceRecord) => {
    return (
      <Chip
        size="small"
        icon={getStatusIcon(record.status)}
        label={record.status}
        sx={{
          bgcolor: alpha(getStatusColor(record.status), 0.1),
          color: getStatusColor(record.status),
          textTransform: 'capitalize'
        }}
      />
    );
  };

  const exportToCSV = () => {
    try {
      const headers = ['Date', 'Waiter', 'Status', 'Check In', 'Check Out', 'Supervisor', 'Created At'];
      const csvData = filteredRecords.map(attendance => [
        new Date(attendance.date).toLocaleDateString(),
        attendance.waiterName || '',
        attendance.status,
        formatTime(attendance.check_in_time),
        formatTime(attendance.check_out_time),
        attendance.createdBy || '',
        new Date(attendance.createdAt).toLocaleString()
      ]);

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `attendance_history_${formatDate(new Date())}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showNotification('Report exported successfully', 'success');
    } catch (error) {
      console.error('Export error:', error);
      showNotification('Failed to export report', 'error');
    }
  };

  const activeFilterCount = Object.values(filters).filter(v => v && v !== 'all' && v !== '').length;

  // Pagination
  const pageCount = Math.ceil(filteredRecords.length / rowsPerPage);
  const paginatedRecords = filteredRecords.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const getDateRangeLabel = () => {
    if (filters.startDate && filters.endDate) {
      return `${formatDate(filters.startDate)} to ${formatDate(filters.endDate)}`;
    }
    return 'Select date range';
  };

  return (
    <IonPage>
      <IonContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
            
            {/* Header */}
            <Paper 
              elevation={0}
              sx={{ 
                borderBottom: `1px solid ${theme.palette.divider}`,
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: 'background.paper'
              }}
            >
              <Container maxWidth="xl">
                <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                  <IconButton onClick={() => history.goBack()}>
                    <BackIcon />
                  </IconButton>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main
                    }}
                  >
                    <TimeIcon />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="h5" fontWeight={600}>
                      Attendance History
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      View waiter attendance history
                    </Typography>
                  </Box>
                  <Chip 
                    label={getDateRangeLabel()}
                    icon={<DateRangeIcon />}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Tooltip title="Export to CSV">
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={exportToCSV}
                        disabled={filteredRecords.length === 0}
                        size="small"
                      >
                        Export
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Refresh">
                    <Button
                      variant="contained"
                      startIcon={<RefreshIcon />}
                      onClick={handleRefresh}
                      disabled={refreshing}
                      size="small"
                    >
                      Refresh
                    </Button>
                  </Tooltip>
                </Stack>
              </Container>
            </Paper>

            <Container maxWidth="xl" sx={{ py: 3 }}>
              
              {/* Filters */}
              <Paper sx={{ mb: 3, overflow: 'hidden' }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Badge badgeContent={activeFilterCount} color="primary">
                        <FilterIcon color="action" />
                      </Badge>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Filters
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                  </Stack>
                </Box>

                {showFilters && (
                  <>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <DatePicker
                            label="Start Date"
                            value={filters.startDate}
                            onChange={(date) => handleFilterChange('startDate', date)}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <DatePicker
                            label="End Date"
                            value={filters.endDate}
                            onChange={(date) => handleFilterChange('endDate', date)}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Waiter</InputLabel>
                            <Select
                              value={filters.waiterId}
                              label="Waiter"
                              onChange={(e) => handleFilterChange('waiterId', e.target.value)}
                            >
                              <MenuItem value="all">All Waiters</MenuItem>
                              {waiters.map(waiter => (
                                <MenuItem key={waiter.id} value={waiter.id}>
                                  {waiter.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                              value={filters.status}
                              label="Status"
                              onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                              <MenuItem value="all">All Statuses</MenuItem>
                              <MenuItem value="present">Present</MenuItem>
                              <MenuItem value="absent">Absent</MenuItem>
                              <MenuItem value="late">Late</MenuItem>
                              <MenuItem value="leave">Leave</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>

                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Search by waiter name"
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" />
                                </InputAdornment>
                              ),
                              endAdornment: filters.search && (
                                <InputAdornment position="end">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleFilterChange('search', '')}
                                  >
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                      </Grid>

                      <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Showing {filteredRecords.length} attendance records
                        </Typography>
                        <Button
                          size="small"
                          onClick={clearFilters}
                          startIcon={<ClearIcon />}
                        >
                          Clear All
                        </Button>
                      </Stack>
                    </Box>
                  </>
                )}
              </Paper>

              {/* History Table */}
              <Paper>
                <TableContainer>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Waiter</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Check In Time</TableCell>
                        <TableCell>Check Out Time</TableCell>
                        <TableCell>Supervisor</TableCell>
                        <TableCell>Created At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      ) : paginatedRecords.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                            <Stack spacing={2} alignItems="center">
                              <TimeIcon sx={{ fontSize: 48, color: theme.palette.grey[300] }} />
                              <Typography color="text.secondary">No attendance records found</Typography>
                              <Button variant="outlined" size="small" onClick={clearFilters}>
                                Clear Filters
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedRecords.map((record) => (
                          <TableRow key={`attendance-${record.id}`} hover>
                            <TableCell>
                              <Typography variant="body2">
                                {new Date(record.date).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                                  {record.waiterName?.charAt(0) || '?'}
                                </Avatar>
                                <Typography variant="body2">
                                  {record.waiterName || 'Unknown'}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              {getStatusChip(record)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                icon={<TimeIcon />}
                                label={formatTime(record.check_in_time)}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                icon={<TimeIcon />}
                                label={formatTime(record.check_out_time)}
                                variant="outlined"
                                color={record.check_out_time ? 'default' : 'warning'}
                              />
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                                  {record.createdBy?.charAt(0) || '?'}
                                </Avatar>
                                <Typography variant="body2">
                                  {record.createdBy || 'Unknown'}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(record.createdAt).toLocaleString()}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {filteredRecords.length > 0 && (
                  <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                      count={pageCount}
                      page={page}
                      onChange={(e, value) => setPage(value)}
                      color="primary"
                      size="small"
                    />
                  </Box>
                )}
              </Paper>
            </Container>

            {/* Refresh Indicator */}
            {refreshing && (
              <Box
                sx={{
                  position: 'fixed',
                  top: 80,
                  right: 24,
                  zIndex: 1100
                }}
              >
                <Paper sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.9), color: 'white' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} color="inherit" />
                    <Typography variant="caption">Refreshing...</Typography>
                  </Stack>
                </Paper>
              </Box>
            )}

            {/* Snackbar */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                {snackbar.message}
              </Alert>
            </Snackbar>

          </Box>
        </LocalizationProvider>
      </IonContent>
    </IonPage>
  );
};

export default AttendanceHistory;